import classNames from "classnames";

import slackLogo from "@/assets/images/uptime-logo-slack.svg";
import {SLACK_ALERT_MOCKS} from "@/pages/integrations/edit/subForms/slack/slackMockData.ts";
import {SlackAlertType, SlackSectionToggles} from "@/pages/integrations/edit/subForms/slackInfo.ts";

import styles from "@/pages/integrations/edit/subForms/slack/SlackPreviewCard.module.scss";

interface SlackPreviewCardProps {
  alertType: SlackAlertType;
  toggles: SlackSectionToggles;
}

export const SlackPreviewCard = ({alertType, toggles}: SlackPreviewCardProps) => {
  const data = SLACK_ALERT_MOCKS[alertType];
  const isDown = alertType === "down";

  const title = isDown ? `🔴 DOWN — failed: ${data.checkName}` : `🟢 UP — recovered: ${data.checkName}`;

  return (
    <div
      className={classNames(styles.previewCard, "position-relative d-flex gap-2 align-items-start w-100")}
      aria-label={`Slack ${alertType} alert preview`}
    >
      <div
        className={classNames(styles.previewAvatar, "d-flex align-items-center justify-content-center overflow-hidden")}
        aria-hidden
      >
        <img src={slackLogo} alt="" className={classNames(styles.previewAvatarImg, "w-100 h-100")} />
      </div>

      <div className="flex-grow-1 min-width-0">
        <div className="d-flex align-items-baseline gap-1 flex-wrap">
          <span className={styles.previewSender}>Uptime.com</span>
          <span className={styles.previewAppPill}>APP</span>
          <span className={styles.previewTimestamp}>{data.timestamp}</span>
        </div>

        <div className="d-flex gap-2 mt-1">
          <span
            className={classNames(
              styles.previewColorBar,
              isDown ? styles.previewColorBarDown : styles.previewColorBarUp
            )}
            aria-hidden
          />

          <div className="flex-grow-1 min-width-0 d-flex flex-column gap-2">
            <p className="pulseTextBodyBold m-0">{title}</p>

            {isDown && data.reason && <p className="m-0">{data.reason}</p>}

            {!isDown && data.recoveredAfter && <p className="m-0">{`is back online after ${data.recoveredAfter}.`}</p>}

            {toggles.monitored_url && data.monitoredUrl && (
              <p className="m-0">
                <span className={styles.previewLineLabel}>URL:</span>
                <span className={styles.previewLink}>{data.monitoredUrl}</span>
              </p>
            )}

            {!isDown && data.recoveredAfter && (
              <p className="m-0">
                <span className={styles.previewLineLabel}>Recovered after:</span>
                {data.recoveredAfter}
              </p>
            )}

            {toggles.additional_details && data.location && (
              <p className="m-0">
                <span className={styles.previewLineLabel}>Location:</span>
                {data.location}
              </p>
            )}

            {data.incidentId && (
              <p className="m-0">
                <span className={styles.previewLineLabel}>Incident:</span>
                {data.incidentId}
              </p>
            )}

            {toggles.tags && data.tags.length > 0 && (
              <p className="m-0">
                <span className={styles.previewLineLabel}>Tags:</span>
                {data.tags.join(", ")}
              </p>
            )}

            {toggles.more_info_links && data.moreInfoUrl && (
              <p className="m-0">
                <span className={styles.previewLineLabel}>More info:</span>
                <span className={styles.previewLink}>{data.moreInfoUrl}</span>
              </p>
            )}

            {toggles.realtime_analysis && data.realtimeAnalysisUrl && (
              <p className="m-0">
                <span className={styles.previewLineLabel}>Real-time analysis:</span>
                <span className={styles.previewLink}>{data.realtimeAnalysisUrl}</span>
              </p>
            )}

            {toggles.additional_details && data.notes && (
              <>
                <p className="m-0">
                  <span className={styles.previewLineLabel}>Notes:</span>
                </p>
                <p className="m-0">{data.notes}</p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
